import { IApiError } from '@frontend/request';

import {
  IExtendExperienceParams,
  IQueryIdentifierError,
  experienceAPIController
} from '@aqx-core/common/api/experience';
import { i18n } from '@aqx-core/common/i18n';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { authenticationService } from '@aqx-core/common/services/auth';
import { experienceService } from '@aqx-core/common/services/experience/service';

interface IExperienceCallbacks {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}
class ExperienceViewController {
  public queryIdentifier = async (
    params: IExtendExperienceParams,
    callbacks: {
      onError: (message: string) => void;
    }
  ) => {
    experienceService.setLoading(true);
    const { onError } = callbacks;

    await experienceAPIController.queryExperienceIdentifier(params, {
      onSuccess: (data) => {
        authenticationService.setPrefilledLogin({ userId: data?.accountId || '', password: '' });
        experienceService.setLoading(false);
      },
      onError: (data) => {
        const { code, message } = data as IApiError;
        if (code === ECommonResponseCode.BAD_REQUEST || code === ECommonResponseCode.AX_1009) {
          onError?.(mapResponseCodeToMessage(ECommonResponseCode.AX_1009, message));
        } else {
          onError?.(mapResponseCodeToMessage(code, message));
        }
        experienceService.setLoading(false);
      }
    });
  };

  public extendTrial = async (
    params: IExtendExperienceParams,
    callbacks: {
      onSuccess: (message: string) => void;
      onError: (message: string) => void;
    }
  ) => {
    experienceService.setLoading(true);
    const { onSuccess } = callbacks;

    await experienceAPIController.extendExperienceThroughSalesforce(params, {
      onSuccess: (data) => {
        if (data?.accountId) {
          authenticationService.setPrefilledLogin({ userId: data.accountId, password: '' });
          onSuccess?.(
            i18n.t(
              'Your demo account has been extended. Please check your email for details or contact us at info@aquariux.com for assistance.'
            )
          );
          experienceService.setLoading(false);
        }
      },
      onError: (error) => {
        const { code, message, result } = error as IQueryIdentifierError;
        const { onError } = callbacks;

        if (code === ECommonResponseCode.AX_1008 && result?.accountId) {
          authenticationService.setPrefilledLogin({ userId: result.accountId || '', password: '' });
          onError?.(mapResponseCodeToMessage(code, message));
        } else if (code === ECommonResponseCode.BAD_REQUEST || code === ECommonResponseCode.AX_1009) {
          onError?.(mapResponseCodeToMessage(ECommonResponseCode.AX_1009, message));
        } else {
          onError?.(mapResponseCodeToMessage(code, message) || i18n.t('Failed to extend trial'));
        }
        experienceService.setLoading(false);
      }
    });
  };

  public queryExperienceIdentifier = (
    redirectId: string,
    extendTrial: boolean | undefined,
    callbacks: IExperienceCallbacks
  ) => {
    if (extendTrial) {
      return this.extendTrial({ experienceAccountKey: redirectId }, callbacks);
    } else {
      return this.queryIdentifier({ experienceAccountKey: redirectId }, callbacks);
    }
  };

  public reset = () => {
    experienceService.reset();
  };
}

export const experienceViewController = new ExperienceViewController();
